<template>
  <div class="tickets-page p-4 bg-light">
    <div class="row g-4 h-100">
      <!-- Tickets List Column -->
      <div :class="selectedTicketInList ? 'col-md-5' : 'col-md-12'">
        <div class="d-flex justify-content-between align-items-center mb-4 px-1">
          <div>
            <div class="col">
              <router-link to="/work/tickets" class="btn btn-primary btn-no-r">
                <span><BaseIcon name="active" /> {{ $t("work.tickets") }}</span>
              </router-link>
              <router-link
                  to="/work/messages"
                  class="btn btn-no-l btn-secondary"
              >
                <span><BaseIcon name="messages" /> {{ $t("work.messages") }}</span>
              </router-link>
            </div>
            <p class="text-muted small mb-0">{{ filteredTickets.length }} tickets found</p>
          </div>
          <div class="d-flex gap-2">
            <v-select
              v-model="filters.status"
              :options="TICKET_STATUSES"
              :reduce="s => s.id"
              label="name"
              placeholder="Status"
              style="width: 150px"
              class="bg-white rounded shadow-sm border-0"
            />
            <v-select
              v-model="filters.priority"
              :options="TICKET_PRIORITIES"
              :reduce="p => p.id"
              label="name"
              placeholder="Priority"
              style="width: 150px"
              class="bg-white rounded shadow-sm border-0"
            />
          </div>
        </div>

        <div class="tickets-list pe-2" style="max-height: calc(-13em + 100vh); overflow-y: auto; overflow-x: hidden;">
          <div v-for="ticket in filteredTickets" :key="ticket.id"
               class="ticket-card card mb-3 border-0 shadow-sm transition-all"
               :class="{ 'active-card shadow': selectedTicketInList?.id === ticket.id }"
               @click="selectTicketForView(ticket)">
            <div class="card-body p-0">
              <div class="d-flex align-items-stretch">
                <!-- Status Sidebar Indicator -->
                <div class="ticket-status-sidebar" :class="getTicketStatusClass(ticket.status)"></div>

                <div class="p-3 flex-grow-1 min-width-0">
                  <!-- Top Row: ID, Title, Status Badge -->
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex align-items-center gap-2 min-width-0">
                      <span class="ticket-id-pill">#{{ ticket.id }}</span>
                      <h6 class="mb-0 text-truncate fw-bold text-dark">{{ ticket.title }}</h6>
                      <span v-if="ticket.order_dispute_id" class="badge bg-danger rounded-pill px-2 py-0 small" style="font-size: 0.6rem">
                        DISPUTE
                      </span>
                    </div>
                    <span :class="['badge rounded-pill px-2 py-1 small fw-medium', getTicketStatusClass(ticket.status)]">
                      {{ getTicketStatusName(ticket.status) }}
                    </span>
                  </div>

                  <!-- Middle Row: Meta Data -->
                  <div class="d-flex flex-wrap align-items-center gap-3 text-muted small mb-3">
                    <div class="meta-item d-flex align-items-center">
                      <i class="bi bi-tag-fill me-1 opacity-50"></i>
                      <span class="text-truncate" style="max-width: 120px">{{ getContactCategoryName(ticket.contact_category_id) }}</span>
                    </div>
                    <div class="meta-item d-flex align-items-center">
                      <div class="avatar-mini-circle me-1">
                        {{ getUserName(ticket.assigned_to_user_id).charAt(0).toUpperCase() }}
                      </div>
                      <span class="text-truncate" style="max-width: 120px">{{ getUserName(ticket.assigned_to_user_id) }}</span>
                    </div>
                    <div class="meta-item d-flex align-items-center">
                      <i class="bi bi-calendar3 me-1 opacity-50"></i>
                      {{ formatDate(ticket.updated_at) }}
                    </div>
                  </div>

                  <!-- Bottom Row: Priority & Tasks -->
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center gap-2">
                      <span :class="['priority-dot', ticket.priority]"></span>
                      <span class="small fw-medium text-capitalize" :class="'text-' + getTicketPriorityClass(ticket.priority).split('-')[1]">
                        {{ getTicketPriorityName(ticket.priority) }}
                      </span>
                    </div>

                    <div v-if="ticket.work_tasks?.length" class="tasks-progress-mini">
                      <div class="d-flex align-items-center gap-2">
                        <div class="progress flex-grow-1" style="width: 40px; height: 4px;">
                          <div class="progress-bar" role="progressbar"
                               :style="{ width: (ticket.work_tasks.filter(t => t.status == 3).length / ticket.work_tasks.length * 100) + '%' }"
                               :class="ticket.work_tasks.filter(t => t.status == 3).length === ticket.work_tasks.length ? 'bg-success' : 'bg-primary'">
                          </div>
                        </div>
                        <span class="small fw-bold text-muted" style="font-size: 0.7rem">
                          {{ ticket.work_tasks.filter(t => t.status == 3).length }}/{{ ticket.work_tasks.length }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!filteredTickets.length" class="text-center p-5 bg-white rounded shadow-sm">
            <i class="bi bi-inbox text-muted display-4"></i>
            <p class="mt-3 text-muted">No tickets found.</p>
          </div>
        </div>
      </div>

      <!-- Ticket Details / Right Panel -->
      <div v-if="selectedTicketInList" class="col-md-7">
        <div class="card border-0 shadow-sm sticky-top" style="top: 24px">
          <div class="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Ticket Details #{{ selectedTicketInList.id }}</h5>
            <div class="d-flex gap-2">
               <button class="btn btn-sm btn-outline-primary rounded-pill" @click="editTicket(selectedTicketInList)">
                <i class="bi bi-pencil me-1"></i> Full Edit
              </button>
              <button class="btn-close" @click="selectedTicketInList = null"></button>
            </div>
          </div>
          <div class="card-body p-4">
            <!-- Order Context -->
            <div v-if="selectedTicketInList.thread?.order || (selectedTicketInList.orderDispute?.orderPackage?.order)" class="mb-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 text-uppercase small fw-bold text-muted">
                  Order Details
                </h6>
                <router-link
                  :to="'/sales/' + (selectedTicketInList.thread?.order?.id || selectedTicketInList.orderDispute?.orderPackage?.order?.id)"
                  class="btn btn-xs btn-outline-secondary"
                >
                  <i class="bi bi-box-arrow-up-right"></i>
                </router-link>
              </div>

              <div class="p-3 bg-light rounded-3 border-0 shadow-sm-hover transition-all">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="fw-bold text-dark">#{{ selectedTicketInList.thread?.order?.pretty_id || selectedTicketInList.orderDispute?.orderPackage?.order?.pretty_id }}</span>
                  <span
                    class="badge rounded-pill px-2 py-1"
                    :class="getOrderStatusClass(selectedTicketInList.thread?.order?.status || selectedTicketInList.orderDispute?.orderPackage?.order?.status)"
                  >
                    {{ getOrderStatusName(selectedTicketInList.thread?.order?.status || selectedTicketInList.orderDispute?.orderPackage?.order?.status) }}
                  </span>
                </div>
                <div class="d-flex flex-wrap gap-3 small text-muted">
                  <div>
                    <i class="bi bi-calendar3 me-1 opacity-50"></i>
                    {{ formatDate(selectedTicketInList.thread?.order?.created_at || selectedTicketInList.orderDispute?.orderPackage?.order?.created_at) }}
                  </div>
                  <div v-if="selectedTicketInList.thread?.order?.total || selectedTicketInList.orderDispute?.orderPackage?.order?.total">
                    <i class="bi bi-currency-dollar me-1 opacity-50"></i>
                    {{ selectedTicketInList.thread?.order?.total || selectedTicketInList.orderDispute?.orderPackage?.order?.total }} {{ selectedTicketInList.thread?.order?.currency || selectedTicketInList.orderDispute?.orderPackage?.order?.currency }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selectedTicketInList.thread" class="alert alert-light border-0 bg-light p-3 mb-4 rounded-3 d-flex align-items-center gap-3">
              <div class="rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center" style="width: 48px; height: 48px">
                <i class="bi bi-chat-quote fs-4 text-primary"></i>
              </div>
              <div class="flex-grow-1">
                <div class="fw-bold text-dark">{{ selectedTicketInList.thread.subject || 'Customer Inquiry' }}</div>
                <div class="small text-muted">{{ selectedTicketInList.thread.email || 'External User' }}</div>
              </div>
              <button class="btn btn-sm btn-outline-primary rounded-pill px-3" @click="goToMessages(selectedTicketInList.message_thread_id)">
                View Thread
              </button>
            </div>

            <div class="mb-4">
              <div class="d-flex align-items-center gap-2 mb-1">
                <h4 class="fw-bold mb-0">{{ selectedTicketInList.title }}</h4>
                <span v-if="selectedTicketInList.order_dispute_id" class="badge bg-danger rounded-pill">
                  ORDER DISPUTE
                </span>
              </div>
              <p class="text-muted">{{ selectedTicketInList.description }}</p>
            </div>

            <div class="row mb-4">
              <div class="col-6 mb-3">
                <label class="small text-muted text-uppercase fw-bold">Status</label>
                <div class="mt-1">
                  <v-select
                    v-model="selectedTicketInList.status"
                    :options="TICKET_STATUSES"
                    :reduce="s => s.id"
                    label="name"
                    @input="updateTicketFromList"
                  />
                </div>
              </div>
              <div class="col-6 mb-3">
                <label class="small text-muted text-uppercase fw-bold">Priority</label>
                <div class="mt-1">
                  <v-select
                    v-model="selectedTicketInList.priority"
                    :options="TICKET_PRIORITIES"
                    :reduce="p => p.id"
                    label="name"
                    @input="updateTicketFromList"
                  />
                </div>
              </div>
            </div>

            <hr />

            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0 fw-bold">Work Tasks</h6>
              <button class="btn btn-sm btn-primary rounded-pill px-3" @click="showTaskForm = true" v-if="!showTaskForm">
                <i class="bi bi-plus-lg me-1"></i> New Task
              </button>
            </div>

            <div v-if="showTaskForm" class="card p-3 mb-3 bg-light border-0 shadow-sm">
              <div class="row g-3">
                <div class="col-md-12">
                  <label class="form-label small fw-bold">Task Name</label>
                  <input type="text" v-model="newTask.name" class="form-control" placeholder="What needs to be done?" />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-bold">Bucket</label>
                  <v-select
                    v-model="newTask.work_bucket_id"
                    :options="workBuckets"
                    :reduce="b => b.id"
                    label="name"
                    placeholder="Bucket"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-bold">Task Group</label>
                  <v-select
                    v-model="newTask.work_task_group_id"
                    :options="availableTaskGroups"
                    :reduce="g => g.id"
                    label="name"
                    placeholder="Group"
                    :disabled="!newTask.work_bucket_id"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-bold">Assignee</label>
                  <v-select
                    v-model="newTask.user_id"
                    :options="users"
                    :reduce="u => u.id"
                    label="full_name"
                    placeholder="Assignee"
                  />
                </div>
                <div class="col-md-12 d-flex gap-2 justify-content-end mt-2">
                  <button class="btn btn-sm btn-light border px-4" @click="showTaskForm = false">Cancel</button>
                  <button class="btn btn-sm btn-primary px-4" @click="saveTask">Create Task</button>
                </div>
              </div>
            </div>

            <div class="tasks-container border rounded-3 bg-white overflow-hidden">
              <div v-for="task in selectedTicketInList.work_tasks" :key="task.id" class="task-item p-3 border-bottom d-flex justify-content-between align-items-center hover-bg-light transition-all">
                <div class="d-flex gap-3 align-items-center">
                  <div class="task-status-icon" :class="getWorkTaskStatusClass(task.status)">
                    <i class="bi" :class="task.status == 3 ? 'bi-check-circle-fill' : 'bi-circle'"></i>
                  </div>
                  <div>
                    <div class="fw-bold mb-0 small">{{ task.name }}</div>
                    <div class="small text-muted d-flex gap-2 align-items-center" style="font-size: 0.7rem">
                      <span>{{ getBucketName(task.work_bucket_id) }}</span>
                      <span class="text-silver">|</span>
                      <span>{{ getUserName(task.user_id) }}</span>
                    </div>
                  </div>
                </div>
                <span :class="['badge rounded-pill', getWorkTaskStatusClass(task.status)]" style="font-size: 0.65rem">
                  {{ getWorkTaskStatusName(task.status) }}
                </span>
              </div>
              <div v-if="!selectedTicketInList.work_tasks?.length" class="text-center py-5 text-muted">
                <p class="small mb-0">No work tasks linked.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Ticket Modal -->
    <div v-if="selectedTicket" class="modal fade show d-block" style="background: rgba(0,0,0,0.5)">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Edit Ticket #{{ selectedTicket.id }}</h5>
            <button type="button" class="btn-close" @click="selectedTicket = null"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedTicket.thread" class="alert alert-light border-0 bg-light p-3 mb-4 rounded-3 d-flex align-items-center gap-3">
              <div class="rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center" style="width: 48px; height: 48px">
                <i class="bi bi-chat-quote fs-4 text-primary"></i>
              </div>
              <div class="flex-grow-1">
                <div class="fw-bold text-dark">{{ selectedTicket.thread.subject || 'Customer Inquiry' }}</div>
                <div class="small text-muted">{{ selectedTicket.thread.email || 'External User' }}</div>
              </div>
              <button class="btn btn-sm btn-outline-primary rounded-pill px-3" @click="goToMessages(selectedTicket.message_thread_id)">
                View Thread
              </button>
            </div>

            <div class="row mb-3">
              <div class="col-md-12 mb-3">
                <label class="form-label">Title</label>
                <input type="text" v-model="selectedTicket.title" class="form-control" />
              </div>
              <div class="col-md-12 mb-3">
                <label class="form-label">Description</label>
                <textarea v-model="selectedTicket.description" class="form-control" rows="3"></textarea>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Status</label>
                <v-select
                  v-model="selectedTicket.status"
                  :options="TICKET_STATUSES"
                  :reduce="s => s.id"
                  label="name"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Priority</label>
                <v-select
                  v-model="selectedTicket.priority"
                  :options="TICKET_PRIORITIES"
                  :reduce="p => p.id"
                  label="name"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Contact Category</label>
                <v-select
                  v-model="selectedTicket.contact_category_id"
                  :options="contactCategories"
                  :reduce="c => c.id"
                  label="display_name"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Assigned To</label>
                <v-select
                  v-model="selectedTicket.assigned_to_user_id"
                  :options="users"
                  :reduce="u => u.id"
                  label="full_name"
                />
              </div>
            </div>

            <hr />
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0 fw-bold">
                <i class="bi bi-list-check me-1"></i>
                Work Tasks
                <span v-if="selectedTicket.work_tasks?.length" class="badge bg-primary rounded-pill ms-2">
                  {{ selectedTicket.work_tasks.length }}
                </span>
              </h6>
              <button class="btn btn-sm btn-primary rounded-pill px-3" @click="showTaskForm = true" v-if="!showTaskForm">
                <i class="bi bi-plus-lg me-1"></i> New Task
              </button>
            </div>

            <div v-if="showTaskForm" class="card p-3 mb-3 bg-light border-0 shadow-sm">
              <div class="row g-3">
                <div class="col-md-12">
                  <label class="form-label small fw-bold">Task Name</label>
                  <input type="text" v-model="newTask.name" class="form-control" placeholder="What needs to be done?" />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-bold">Bucket</label>
                  <v-select
                    v-model="newTask.work_bucket_id"
                    :options="workBuckets"
                    :reduce="b => b.id"
                    label="name"
                    placeholder="Bucket"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-bold">Task Group</label>
                  <v-select
                    v-model="newTask.work_task_group_id"
                    :options="availableTaskGroups"
                    :reduce="g => g.id"
                    label="name"
                    placeholder="Group"
                    :disabled="!newTask.work_bucket_id"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-bold">Assignee</label>
                  <v-select
                    v-model="newTask.user_id"
                    :options="users"
                    :reduce="u => u.id"
                    label="full_name"
                    placeholder="Assignee"
                  />
                </div>
                <div class="col-md-12 d-flex gap-2 justify-content-end mt-2">
                  <button class="btn btn-sm btn-light border px-4" @click="showTaskForm = false">Cancel</button>
                  <button class="btn btn-sm btn-primary px-4" @click="saveTask">Create Task</button>
                </div>
              </div>
            </div>

            <div class="tasks-container border rounded-3 bg-white overflow-hidden">
              <div v-for="task in selectedTicket.work_tasks" :key="task.id" class="task-item p-3 border-bottom d-flex justify-content-between align-items-center hover-bg-light transition-all">
                <div class="d-flex gap-3 align-items-center">
                  <div class="task-status-icon" :class="getWorkTaskStatusClass(task.status)">
                    <i class="bi" :class="task.status == 3 ? 'bi-check-circle-fill' : 'bi-circle'"></i>
                  </div>
                  <div>
                    <div class="fw-bold mb-0">{{ task.name }}</div>
                    <div class="small text-muted d-flex gap-2 align-items-center">
                      <span class="d-flex align-items-center gap-1">
                        <i class="bi bi-folder2"></i> {{ getBucketName(task.work_bucket_id) }}
                      </span>
                      <span class="text-silver">|</span>
                      <span class="d-flex align-items-center gap-1">
                        <i class="bi bi-person"></i> {{ getUserName(task.user_id) }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-3">
                   <span :class="['badge rounded-pill px-3', getWorkTaskStatusClass(task.status)]">
                    {{ getWorkTaskStatusName(task.status) }}
                  </span>
                </div>
              </div>
              <div v-if="!selectedTicket.work_tasks?.length" class="text-center py-5 text-muted">
                <i class="bi bi-clipboard-x fs-2 d-block mb-2 opacity-50"></i>
                No work tasks linked to this ticket.
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="selectedTicket = null">Cancel</button>
            <button type="button" class="btn btn-primary" @click="updateTicket">Update Ticket</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/modules/http";
import date from "@/modules/date";
import {
  TICKET_STATUSES,
  TICKET_PRIORITIES,
  TICKET_CATEGORIES
} from "../../../constants/tickets";
import BaseIcon from "../../../components/icons/BaseIcon.vue";

export default {
  name: "Tickets",
  components: {BaseIcon},
  data() {
    return {
      tickets: [],
      users: [],
      workBuckets: [],
      contactCategories: [],
      selectedTicket: null,
      selectedTicketInList: null,
      filters: {
        status: null,
        priority: null
      },
      showTaskForm: false,
      newTask: {
        name: "",
        work_bucket_id: null,
        work_task_group_id: null,
        user_id: null
      },
      TICKET_STATUSES,
      TICKET_PRIORITIES,
      TICKET_CATEGORIES
    };
  },
  computed: {
    filteredTickets() {
      return this.tickets.filter(ticket => {
        if (this.filters.status && ticket.status !== this.filters.status) return false;
        if (this.filters.priority && ticket.priority !== this.filters.priority) return false;
        return true;
      });
    },
    availableTaskGroups() {
      if (!this.newTask.work_bucket_id) return [];
      const bucket = this.workBuckets.find(b => b.id === this.newTask.work_bucket_id);
      return bucket ? (bucket.work_task_groups || bucket.workTaskGroups || []) : [];
    }
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    async loadInitialData() {
      try {
        const [usersData, bucketsData] = await Promise.all([
          http.fetch("/users"),
          http.fetch("/work/buckets")
        ]);
        this.users = (usersData || []).map(u => ({
          ...u,
          full_name: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
        }));
        this.workBuckets = Array.isArray(bucketsData) ? bucketsData : bucketsData.data || [];
        await this.loadTickets();
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    },
    async loadTickets() {
      try {
        const data = await http.fetch("/tickets") || {};
        // Handle both direct array, {tickets: []} and paginated {data: []}
        this.tickets = data.tickets || data.data || [];
        // Handle categories or contactCategories from backend
        const rawCategories = data.categories || data.contactCategories || [];
        this.contactCategories = rawCategories.map(c => ({
          ...c,
          display_name: c.translation?.name || c.name
        }));
      } catch (error) {
        console.error("Error loading tickets:", error);
      }
    },
    getTicketStatusName(id) {
      return TICKET_STATUSES.find(s => s.id === id)?.name || id;
    },
    getTicketStatusClass(id) {
      return TICKET_STATUSES.find(s => s.id === id)?.class || "bg-secondary";
    },
    getTicketPriorityName(id) {
      return TICKET_PRIORITIES.find(p => p.id === id)?.name || id;
    },
    getTicketPriorityClass(id) {
      return TICKET_PRIORITIES.find(p => p.id === id)?.class || "bg-secondary";
    },
    getWorkTaskStatusName(status) {
      const names = {
        0: "To Do",
        1: "In Progress",
        2: "On Hold",
        3: "Completed",
        4: "Cancelled",
      };
      return names[status] || "Unknown";
    },
    getWorkTaskStatusClass(status) {
      const map = {
        0: "bg-light text-dark border",
        1: "bg-info text-white",
        2: "bg-warning text-dark",
        3: "bg-success text-white",
        4: "bg-danger text-white",
      };
      return map[status] || "bg-secondary";
    },
    getUserName(id) {
      if (!id) return "Unassigned";
      const user = this.users.find(u => u.id == id);
      return user ? user.full_name : "Unknown User";
    },
    getContactCategoryName(id) {
      const cat = this.contactCategories.find(c => c.id == id);
      return cat ? cat.display_name : "General";
    },
    getBucketName(id) {
      const bucket = this.workBuckets.find(b => b.id == id);
      return bucket ? bucket.name : "Unknown Bucket";
    },
    formatDate(val) {
      return date.format(val, true, false);
    },
    selectTicketForView(ticket) {
      this.selectedTicketInList = ticket;
      this.showTaskForm = false;
    },
    async updateTicketFromList() {
      if (!this.selectedTicketInList) return;
      try {
        await http.fetch(`/tickets/${this.selectedTicketInList.id}`, this.selectedTicketInList, true, "PUT");
        await this.loadTickets();

        // Refresh selected ticket in list view if active
        if (this.selectedTicketInList) {
           const updated = this.tickets.find(t => t.id === this.selectedTicketInList.id);
           if (updated) this.selectedTicketInList = updated;
        }
      } catch (error) {
        console.error("Error updating ticket from list:", error);
      }
    },
    editTicket(ticket) {
      this.selectedTicket = JSON.parse(JSON.stringify(ticket));
      this.showTaskForm = false;
      this.newTask = { name: "", work_bucket_id: null, work_task_group_id: null, user_id: null };
    },
    async updateTicket() {
      try {
        await http.fetch(`/tickets/${this.selectedTicket.id}`, this.selectedTicket, true, "PUT");
        this.selectedTicket = null;
        await this.loadTickets();
      } catch (error) {
        console.error("Error updating ticket:", error);
      }
    },
    async saveTask() {
      if (!this.newTask.name || !this.newTask.work_bucket_id || !this.newTask.work_task_group_id) return;
      const ticketId = this.selectedTicket?.id || this.selectedTicketInList?.id;
      if (!ticketId) return;

      try {
        const data = await http.fetch("/work/tasks", {
          name: this.newTask.name,
          user_id: this.newTask.user_id,
          work_task_group_id: this.newTask.work_task_group_id,
          ticket_id: ticketId
        }, true, "POST");
        const task = data.task || data.data || data;

        if (this.selectedTicket) {
          if (!this.selectedTicket.work_tasks) this.selectedTicket.work_tasks = [];
          this.selectedTicket.work_tasks.push(task);
        }

        this.showTaskForm = false;
        this.newTask = { name: "", work_bucket_id: null, work_task_group_id: null, user_id: null };
        await this.loadTickets();

        // Refresh selected ticket in list view if active
        if (this.selectedTicketInList && this.selectedTicketInList.id === ticketId) {
          const updated = this.tickets.find(t => t.id === ticketId);
          if (updated) this.selectedTicketInList = updated;
        }
      } catch (error) {
        console.error("Error saving task:", error);
      }
    },
    goToMessages(threadId) {
      this.$router.push({ path: "/work/messages", query: { thread: threadId } });
    },
    getOrderStatusName(status) {
      const statuses = {
        0: "Pending",
        1: "Processing",
        2: "On Hold",
        3: "Completed",
        4: "Cancelled",
        5: "Refunded",
        6: "Failed"
      };
      return statuses[status] || "Unknown";
    },
    getOrderStatusClass(status) {
      const classes = {
        0: "bg-warning",
        1: "bg-info",
        2: "bg-secondary",
        3: "bg-success",
        4: "bg-danger",
        5: "bg-dark",
        6: "bg-danger"
      };
      return classes[status] || "bg-light";
    }
  }
};
</script>

<style scoped>
  .tickets-page {
    max-height: calc(100vh - 6em);
    min-height: calc(100vh - 6em);
  }
  .ticket-card {
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    background: white;
    border-radius: 12px;
    overflow: hidden;
  }
  .ticket-card:hover {
    transform: translateX(4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
    background: #fff;
  }
  .active-card {
    background-color: #fff !important;
    border: 1px solid rgba(var(--bs-primary-rgb), 0.3) !important;
    box-shadow: 0 4px 12px rgba(var(--bs-primary-rgb), 0.1) !important;
  }
  .ticket-status-sidebar {
    width: 4px;
    flex-shrink: 0;
  }
  .ticket-id-pill {
    background: #f1f3f5;
    color: #495057;
    font-weight: 700;
    font-size: 0.7rem;
    padding: 2px 8px;
    border-radius: 6px;
    border: 1px solid #e9ecef;
  }
  .avatar-mini-circle {
    width: 20px;
    height: 20px;
    background: #e9ecef;
    color: #495057;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: bold;
    border: 1px solid #dee2e6;
  }
  .priority-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }
  .priority-dot.urgent { background-color: #dc3545; box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2); }
  .priority-dot.high { background-color: #fd7e14; }
  .priority-dot.normal { background-color: #0d6efd; }
  .priority-dot.low { background-color: #6c757d; }

  .tasks-progress-mini .progress {
    background-color: #e9ecef;
    border-radius: 10px;
  }

  .meta-item {
    line-height: 1;
  }

  /* Badge colors refinement */
  .badge.bg-warning { background-color: #fff3cd !important; color: #856404 !important; border: 1px solid #ffeeba !important; }
  .badge.bg-success { background-color: #d4edda !important; color: #155724 !important; border: 1px solid #c3e6cb !important; }
  .badge.bg-primary { background-color: #cfe2ff !important; color: #084298 !important; border: 1px solid #b6d4fe !important; }
  .badge.bg-info { background-color: #cff4fc !important; color: #055160 !important; border: 1px solid #b6effb !important; }
  .badge.bg-secondary { background-color: #e2e3e5 !important; color: #41464b !important; border: 1px solid #d6d8db !important; }

  .thread-info-bar {
    border-left-width: 3px !important;
  }
  .task-item:last-child {
    border-bottom: 0 !important;
  }
  .hover-bg-light:hover {
    background-color: #f8f9fa;
  }
  .text-silver {
    color: #ccc;
  }
  .task-status-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 0.75rem;
  }
  .transition-all {
    transition: all 0.2s ease-in-out;
  }
  .min-width-0 {
    min-width: 0;
  }
  .cursor-pointer {
    cursor: pointer;
  }
</style>
